import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { PricingPlans } from '@/components/pricing/PricingPlans';
import { STRIPE_PRODUCTS } from '@/stripe-config';
import { stripeService } from '@/services/stripe-service';
import { useAuth } from '@/hooks/useAuth';
import { useAnalytics } from '@/hooks/useAnalytics';
import { metaPixel } from '@/utils/meta-pixel';
import { ProPricingBanner } from './ProPricingBanner';

export const ProPricing: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const analytics = useAnalytics();

  React.useEffect(() => {
    metaPixel.trackPricingView();
  }, []);

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const handleSelectPlan = async (plan: typeof STRIPE_PRODUCTS[0]) => {
    if (!isAuthenticated) {
      navigate(`/auth?mode=signup&redirect=/pricing`);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSelectedPlan(plan.id);

      analytics.trackEvent({
        category: plan.mode === 'subscription' ? 'subscription' : 'payment',
        action: 'select_plan',
        label: plan.id,
        custom_parameters: {
          price: plan.price,
          plan_name: plan.name
        }
      });

      const { stripeService } = await import('@/services/stripe-service');

      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/success?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${baseUrl}/pricing`;

      const { url } = await stripeService.createCheckoutSession({
        priceId: plan.priceId,
        mode: plan.mode,
        successUrl,
        cancelUrl,
        creditsAmount: plan.mode === 'payment' ? 1 : undefined
      });

      window.location.href = url;
    } catch (error: any) {
      console.error('Purchase error:', error);
      setError(error.message || 'Erreur lors de la création de la session de paiement');

      analytics.trackEvent({
        action: 'error',
        category: 'payment',
        label: 'checkout_creation_failed',
        custom_parameters: {
          error_message: error.message,
          plan_id: plan.id
        }
      });
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  return (
    <section className="py-20 bg-white" id="pricing">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Choisissez votre formule
          </h2>
        </div>

        {error && (
          <div className="max-w-2xl mx-auto mb-8 p-4 bg-gray-50 border border-gray-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-gray-500 flex-shrink-0" />
            <p className="text-gray-700">{error}</p>
          </div>
        )}

        <PricingPlans
          onSelectPlan={handleSelectPlan}
          selectedPlanId={selectedPlan}
          loading={loading}
        />

        <ProPricingBanner />
      </div>
    </section>
  );
};

export default ProPricing;
